<template>
	<div>
		<div class="d-flex flex-wrap align-items-center gap-2 mb-2">
			<span class="small text-body-secondary">{{
				technician.role || "Technician"
			}}</span>
			<span
				class="badge rounded-pill"
				:class="technician.is_active ? 'bg-success' : 'bg-secondary'"
			>
				{{ technician.is_active ? "Active" : "Inactive" }}
			</span>
			<button
				type="button"
				class="btn btn-sm btn-primary ms-auto"
				@click="emit('assignWork', technician.id)"
			>
				<i class="bi bi-plus-lg me-1"></i>Assign work
			</button>
		</div>

		<div class="row g-3">
			<div class="col-12 col-lg-7">
				<h3 class="h6">Live assignments</h3>
				<p
					v-if="liveAssignments.length === 0"
					class="small text-body-secondary mb-0"
				>
					No live work. Everything this technician held is settled.
				</p>
				<div v-else class="list-group list-group-flush">
					<button
						v-for="assignment in liveAssignments"
						:key="assignment.id"
						type="button"
						class="list-group-item list-group-item-action py-1 px-1"
						@click="emit('select', { kind: 'assignment', id: assignment.id })"
					>
						<div
							class="d-flex justify-content-between align-items-center gap-2"
						>
							<span class="text-truncate">
								<span
									class="badge rounded-pill"
									:class="kindBadgeClass(assignment.kind)"
								>
									{{ kindLabel(assignment.kind) }}
								</span>
								<span class="ms-2">{{ targetLabel(assignment, sites) }}</span>
							</span>
							<span
								class="badge rounded-pill"
								:class="stateBadgeClass(assignment.state)"
							>
								{{ stateLabel(assignment.state) }}
							</span>
						</div>
						<div class="small text-body-secondary text-truncate">
							#{{ assignment.id }} · {{ assignmentWindowText(assignment) }}
						</div>
					</button>
				</div>
			</div>

			<div class="col-12 col-lg-5">
				<h3 class="h6">Routes</h3>
				<p
					v-if="technicianRoutes.length === 0"
					class="small text-body-secondary mb-0"
				>
					No routes for this technician.
				</p>
				<div v-else class="list-group list-group-flush">
					<button
						v-for="route in technicianRoutes"
						:key="route.id"
						type="button"
						class="list-group-item list-group-item-action py-1 px-1"
						@click="emit('select', { kind: 'route', id: route.id })"
					>
						<div
							class="d-flex justify-content-between align-items-center gap-2"
						>
							<span class="text-truncate">
								<span
									class="route-swatch me-1"
									:style="{ background: routeColor(route.id) }"
								></span>
								#{{ route.id }} · {{ route.members.length }} stops
							</span>
							<span
								class="badge rounded-pill"
								:class="routeBadgeClass(route.state)"
							>
								{{ route.state }}
							</span>
						</div>
						<div class="small text-body-secondary text-truncate">
							{{ routeWindowText(route) }}
						</div>
					</button>
				</div>
			</div>
		</div>

		<!-- Settled work is history, so it sits behind a summary rather than in
		     the live list; it is still readable, which is what keeps a completed
		     row's recorded facts reachable after the queue's state filter is
		     gone. -->
		<details v-if="settledCount > 0" class="mt-3">
			<summary class="h6">Settled work ({{ settledCount }})</summary>
			<div class="list-group list-group-flush mt-2">
				<button
					v-for="assignment in settledAssignments"
					:key="assignment.id"
					type="button"
					class="list-group-item list-group-item-action py-1 px-1"
					@click="emit('select', { kind: 'assignment', id: assignment.id })"
				>
					<div class="d-flex justify-content-between align-items-center gap-2">
						<span class="text-truncate">
							#{{ assignment.id }} ·
							{{ targetLabel(assignment, sites) }}
						</span>
						<span
							class="badge rounded-pill"
							:class="stateBadgeClass(assignment.state)"
						>
							{{ stateLabel(assignment.state) }}
						</span>
					</div>
					<div class="small text-body-secondary text-truncate">
						{{ kindLabel(assignment.kind) }} ·
						{{ assignmentWindowText(assignment) }}
					</div>
				</button>
			</div>
			<p v-if="settledCount > settledAssignments.length" class="form-text mb-0">
				Showing the {{ settledAssignments.length }} most recently settled of
				{{ settledCount }}.
			</p>
		</details>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type {
	MapReviewSiteRow,
	SyncAssignment,
	SyncRoute,
	User,
} from "@/type/api";

import { routeColor, type Selection } from "./model";
import {
	assignmentWindowText,
	kindBadgeClass,
	kindLabel,
	routeBadgeClass,
	routeWindowText,
	stateBadgeClass,
	stateLabel,
	targetLabel,
} from "./wire";

const props = defineProps<{
	technician: User;
	assignments: SyncAssignment[];
	routes: SyncRoute[];
	sites: MapReviewSiteRow[];
}>();

const emit = defineEmits<{
	select: [selection: Selection];
	assignWork: [technicianId: number];
}>();

const liveAssignments = computed(() =>
	props.assignments
		.filter(
			(row) =>
				row.technicianId === props.technician.id &&
				row.state !== "completed" &&
				row.state !== "cancelled",
		)
		.sort(
			(left, right) =>
				new Date(left.startAt ?? left.createdAt).getTime() -
				new Date(right.startAt ?? right.createdAt).getTime(),
		),
);

const technicianRoutes = computed(() =>
	props.routes
		.filter((route) => route.technicianId === props.technician.id)
		.sort(
			(left, right) =>
				new Date(right.windowStart).getTime() -
				new Date(left.windowStart).getTime(),
		),
);

const settled = computed(() =>
	props.assignments
		.filter(
			(row) =>
				row.technicianId === props.technician.id &&
				(row.state === "completed" || row.state === "cancelled"),
		)
		.sort(
			(left, right) =>
				new Date(right.completedAt ?? right.updatedAt).getTime() -
				new Date(left.completedAt ?? left.updatedAt).getTime(),
		),
);

const settledCount = computed(() => settled.value.length);
const settledAssignments = computed(() => settled.value.slice(0, 20));
</script>

<style scoped>
.route-swatch {
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 2px;
}
</style>
